import { CSSProperties, useEffect, useState } from "react";
import BottomUpModalWrapper from "./BottomUpModalWrapper";
import GuideSearchTester from "../pages/Test/GuideSearchTester";
import {
  Artist,
  Collections,
  Guide,
  PendingBrewingTransaction,
  SongRequest,
} from "../../types/type";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { useRecoilValue } from "recoil";
import { artistListState } from "../../atom/atom";
import { generateIdByNowDate, makeRandomString } from "../../tools/tools";
import { db } from "../../firebase/firebaseConfig";

type SearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  activeSongRequest: SongRequest;
};

const AWS_URL =
  "https://0kn3823294.execute-api.ap-northeast-2.amazonaws.com/prod/request";

export const toISOString = (
  timestamp: Timestamp | Date | number | string | undefined | null
): string => {
  if (!timestamp) {
    return new Date().toISOString();
  }

  try {
    // Firebase Timestamp
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate().toISOString();
    }

    // Date object
    if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }

    // Unix timestamp (number)
    if (typeof timestamp === "number") {
      return new Date(timestamp).toISOString();
    }

    // ISO string
    if (typeof timestamp === "string") {
      return new Date(timestamp).toISOString();
    }

    throw new Error("Invalid timestamp format");
  } catch (error) {
    console.error("Timestamp conversion error:", error);
    return new Date().toISOString(); // 에러 시 현재 시간 반환
  }
};

type ApiRequestData = PendingBrewingTransaction;

type ApiStatus = {
  loading: boolean;
  success: boolean;
  error: string | null;
};

const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  activeSongRequest,
}) => {
  const [apiRequestData, setApiRequestData] = useState<ApiRequestData | null>(
    null
  );
  const [apiStatus, setApiStatus] = useState<ApiStatus>({
    loading: false,
    success: false,
    error: null,
  });
  const totalArtistList = useRecoilValue(artistListState);

  const checkExistingTransaction = async (data: PendingBrewingTransaction) => {
    const pendingRef = collection(db, Collections.PENDING_BREWING_TRANSACTION);
    const q = query(
      pendingRef,
      where("model", "==", data.model),
      where("guideTitle", "==", data.guideTitle),
      where("guideId", "==", data.guideId),
      where("isMaleSinger", "==", data.isMaleSinger)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  console.log("activeSongRequest : ", activeSongRequest);

  const handleSendApi = async () => {
    if (!apiRequestData) {
      alert("요청 데이터가 없습니다.");
      return;
    }

    if (!apiRequestData.model) {
      alert("아티스트를 선택해주세요.");
      return;
    }

    if (!apiRequestData.guideId || !apiRequestData.guideTitle) {
      alert("가이드를 선택해주세요.");
      return;
    }

    setApiStatus({ loading: true, success: false, error: null });

    try {
      const hasDuplicate = await checkExistingTransaction(apiRequestData);

      if (hasDuplicate) {
        setApiStatus({
          loading: false,
          success: false,
          error: "이미 동일한 요청이 존재합니다.",
        });
        return;
      }

      const pendingTransaction: PendingBrewingTransaction = {
        id: generateIdByNowDate() + makeRandomString(6),
        songRequestId: apiRequestData.songRequestId,
        existingArtist: activeSongRequest.existingArtist,
        status: "pending",
        userId: activeSongRequest.userId,
        userName: activeSongRequest.userName,
        userToken: activeSongRequest.userToken,
        model: apiRequestData.model,
        isMaleSinger: apiRequestData.isMaleSinger,
        guideTitle: apiRequestData.guideTitle,
        guideId: apiRequestData.guideId,
        requestAt: Timestamp.now(),
      };

      // Firestore에 document 추가
      await setDoc(
        doc(db, Collections.PENDING_BREWING_TRANSACTION, pendingTransaction.id),
        pendingTransaction
      );

      setApiStatus({ loading: false, success: true, error: null });
      onClose();
    } catch (error) {
      console.error("Error creating pending transaction:", error);
      setApiStatus({
        loading: false,
        success: false,
        error: "요청 처리 중 오류가 발생했습니다.",
      });
    }
  };

  console.log("apiRequestData : ", apiRequestData);

  useEffect(() => {
    if (activeSongRequest) {
      console.log("setApiRequestData !!!");
      setApiRequestData({
        id: "", // 실제 저장 시 생성
        songRequestId: activeSongRequest.songRequestId,
        existingArtist: activeSongRequest.existingArtist,
        status: "pending",
        userId: activeSongRequest.userId,
        userName: activeSongRequest.userName,
        userToken: activeSongRequest.userToken,
        model: activeSongRequest.existingArtist?.modelName || "",
        isMaleSinger: activeSongRequest.existingArtist?.isMale || false,
        guideTitle: "",
        guideId: "",
        requestAt: Timestamp.now(),
      });
    }
  }, [activeSongRequest]);

  const handleClickGuide = (guide: Guide) => {
    setApiRequestData((prev) =>
      prev
        ? { ...prev, guideId: guide.driveId, guideTitle: guide.guideTitle }
        : prev
    );
  };

  const handleArtistSelect = (artist: Artist) => {
    console.log("handleArtistSelect artist.artistName : ", artist.artistName);
    console.log("handleArtistSelect artist.isMale : ", artist.isMale);
    setApiRequestData((prev) =>
      prev
        ? {
            ...prev,
            model: artist.modelName || "",
            isMaleSinger: artist.isMale,
          }
        : prev
    );
  };

  return (
    <>
      <BottomUpModalWrapper isOpen={isOpen} onClose={onClose}>
        <div style={styles.container}>
          {/* 왼쪽: 가이드 검색 */}
          <div style={styles.searchSection}>
            <GuideSearchTester
              onClickResult={handleClickGuide}
              genderMode={
                activeSongRequest.existingArtist?.isMale ? "male" : "female"
              }
              autoFocus={true}
              titleOff={true}
            />
          </div>

          {/* 오른쪽: 아티스트 선택 및 정보 */}
          <div style={styles.infoSection}>
            {/* 아티스트 선택 스크롤 영역 */}
            <div style={styles.artistScrollContainer}>
              <div style={styles.artistGrid}>
                {totalArtistList?.map((artist) => (
                  <button
                    key={artist.artistName}
                    onClick={() => handleArtistSelect(artist)}
                    style={{
                      ...styles.artistButton,
                      ...(apiRequestData?.model === artist.modelName
                        ? styles.artistButtonSelected
                        : {}),
                    }}
                  >
                    {artist.artistName}
                  </button>
                ))}
              </div>
            </div>

            {/* 요청 정보 표시 */}
            <div style={styles.requestInfo}>
              <div style={styles.infoGrid}>
                <div style={styles.infoRow}>
                  <span style={styles.label}>요청 ID:</span>
                  <span style={styles.value}>
                    {apiRequestData?.songRequestId}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>사용자 ID:</span>
                  <span style={styles.value}>{apiRequestData?.userId}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>아티스트:</span>
                  <span style={styles.value}>
                    {apiRequestData?.model || "-"}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>성별:</span>
                  <span style={styles.value}>
                    {apiRequestData?.isMaleSinger ? "남성" : "여성"}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>가이드명:</span>
                  <span style={styles.value}>
                    {apiRequestData?.guideTitle || "-"}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>가이드 ID:</span>
                  <span style={styles.value}>
                    {apiRequestData?.guideId || "-"}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>요청시간:</span>
                  <span style={styles.value}>
                    {apiRequestData?.requestAt
                      ? new Date(apiRequestData.requestAt).toLocaleString()
                      : "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* API 상태 표시 */}
            <div style={styles.statusSection}>
              {apiStatus.loading && (
                <div style={styles.statusMessage}>요청 처리 중...</div>
              )}
              {apiStatus.success && (
                <div style={{ ...styles.statusMessage, color: "#4CAF50" }}>
                  전송 완료
                </div>
              )}
              {apiStatus.error && (
                <div style={{ ...styles.statusMessage, color: "#f44336" }}>
                  {apiStatus.error}
                </div>
              )}
            </div>

            <div style={styles.buttonContainer}>
              <button
                onClick={handleSendApi}
                disabled={!apiRequestData || apiStatus.loading}
                style={{
                  ...styles.submitButton,
                  ...(!apiRequestData || apiStatus.loading
                    ? styles.buttonDisabled
                    : {}),
                }}
              >
                {apiStatus.loading ? "처리중..." : "전송"}
              </button>
              <button onClick={onClose} style={styles.closeButton}>
                닫기
              </button>
            </div>
          </div>
        </div>
      </BottomUpModalWrapper>
    </>
  );
};

const styles: Record<string, CSSProperties> = {
  container: {
    display: "flex",
    width: "100%",
    height: "340px",
    padding: "12px",
    gap: "12px",
  },
  searchSection: {
    width: "40%",
    backgroundColor: "#fff",
    borderRadius: "6px",
    padding: "8px",
  },
  infoSection: {
    width: "60%",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    backgroundColor: "#fff",
    borderRadius: "6px",
    padding: "8px",
  },
  artistScrollContainer: {
    width: "100%",
    padding: "2px",
  },
  artistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(50px, 1fr))", // 더 작은 최소 너비
    gap: "2px",
  },
  artistButton: {
    padding: "1px 4px",
    backgroundColor: "#f5f5f5",
    border: "1px solid #ddd",
    borderRadius: "3px",
    cursor: "pointer",
    fontSize: "10px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    height: "16px",
    lineHeight: "14px",
  },
  artistButtonSelected: {
    backgroundColor: "#e3f2fd",
    borderColor: "#2196f3",
    color: "#2196f3",
  },
  requestInfo: {
    backgroundColor: "#f8f8f8",
    padding: "6px",
    borderRadius: "4px",
    height: "160px", // 고정 높이
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)", // 2열 그리드로 변경
    gap: "4px 8px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "11px",
    padding: "1px 4px",
    backgroundColor: "white",
    borderRadius: "2px",
  },
  label: {
    color: "#666",
    fontWeight: "500",
    minWidth: "60px",
  },
  value: {
    color: "#333",
    maxWidth: "calc(100% - 70px)", // 라벨 너비를 뺀 나머지
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "right",
  },
  statusSection: {
    minHeight: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f8f8",
    borderRadius: "4px",
    padding: "0 4px",
  },
  statusMessage: {
    fontSize: "11px",
    textAlign: "center",
  },
  buttonContainer: {
    display: "flex",
    gap: "6px",
    marginTop: "auto",
  },
  submitButton: {
    flex: 1,
    padding: "6px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    height: "28px",
  },
  buttonDisabled: {
    backgroundColor: "#cccccc",
    cursor: "not-allowed",
  },
  closeButton: {
    flex: 1,
    padding: "6px",
    backgroundColor: "#f44336",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    height: "28px",
  },
};

export default SearchModal;
